// SMSF Validation Engine - Real-time validation for SMSF Calculator
// Provides comprehensive validation rules and error checking

import React from 'react';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import { FinancialConfig } from './FinancialConfig.js';

export const SMSFValidationEngine = {
    // Validation severity levels
    SEVERITY: {
        ERROR: 'error',      // Critical - prevents submission
        WARNING: 'warning',  // Important but non-blocking
        INFO: 'info'        // Informational only
    },

    // Contribution validation
    validateContributions(concessional, nonConcessional, totalSuperBalance, age) {
        const errors = [];
        const warnings = [];
        const info = [];

        // Concessional contributions cap
        const concessionalCap = SMSF_RATES_CONFIG.contributionCaps.concessional.limit;
        if (concessional > concessionalCap) {
            errors.push({
                field: 'concessional',
                severity: this.SEVERITY.ERROR,
                message: `Concessional contributions ($${concessional.toLocaleString()}) exceed annual cap of $${concessionalCap.toLocaleString()}`,
                remedy: `Reduce contributions by $${(concessional - concessionalCap).toLocaleString()} or carry forward unused amounts`,
                code: 'CONC_CAP_EXCEEDED'
            });
        }

        // Non-concessional contributions cap
        const nonConcessionalCap = SMSF_RATES_CONFIG.contributionCaps.nonConcessional.limit;
        const tsbThreshold = SMSF_RATES_CONFIG.contributionCaps.totalSuperBalanceThreshold;
        
        if (totalSuperBalance >= tsbThreshold) {
            if (nonConcessional > 0) {
                errors.push({
                    field: 'nonConcessional',
                    severity: this.SEVERITY.ERROR,
                    message: `Non-concessional contributions not allowed when TSB exceeds $${tsbThreshold.toLocaleString()}`,
                    remedy: 'Under current ATO rules, when total super balance exceeds this threshold, non-concessional contributions are not permitted. This is general information only, not advice. Seek a licensed financial adviser for options suited to your situation.',
                    code: 'TSB_THRESHOLD_EXCEEDED'
                });
            }
        } else if (nonConcessional > nonConcessionalCap) {
            // Check for bring-forward eligibility
            const bringForwardCap = nonConcessionalCap * 3;
            if (nonConcessional <= bringForwardCap && age < 75) {
                warnings.push({
                    field: 'nonConcessional',
                    severity: this.SEVERITY.WARNING,
                    message: `Non-concessional contributions ($${nonConcessional.toLocaleString()}) exceed annual cap`,
                    remedy: `Bring-forward rule triggered - 3-year cap of $${bringForwardCap.toLocaleString()} applies`,
                    code: 'BRING_FORWARD_TRIGGERED'
                });
            } else {
                errors.push({
                    field: 'nonConcessional',
                    severity: this.SEVERITY.ERROR,
                    message: `Non-concessional contributions ($${nonConcessional.toLocaleString()}) exceed cap`,
                    remedy: `Maximum ${age >= 75 ? 'annual' : 'bring-forward'} cap: $${age >= 75 ? nonConcessionalCap : bringForwardCap.toLocaleString()}`,
                    code: 'NON_CONC_CAP_EXCEEDED'
                });
            }
        }

        // Age-based contribution eligibility
        if (age >= 75) {
            info.push({
                field: 'age',
                severity: this.SEVERITY.INFO,
                message: 'Contributions require work test or exemption for members aged 75+',
                remedy: 'Ensure work test requirements are met',
                code: 'WORK_TEST_REQUIRED'
            });
        }

        return { errors, warnings, info, isValid: errors.length === 0 };
    },

    // Transfer Balance Cap validation
    validateTBC(currentTBC, newTransfer, pensionBalance) {
        const errors = [];
        const warnings = [];
        
        const tbcCap = SMSF_RATES_CONFIG.transferBalanceCap.generalTBC;
        const totalTBC = currentTBC + newTransfer;

        if (totalTBC > tbcCap) {
            errors.push({
                field: 'tbc',
                severity: this.SEVERITY.ERROR,
                message: `Transfer Balance Cap exceeded: $${totalTBC.toLocaleString()} > $${tbcCap.toLocaleString()}`,
                remedy: `Reduce pension transfer by $${(totalTBC - tbcCap).toLocaleString()}`,
                code: 'TBC_EXCEEDED'
            });
        }

        // Warning at 90% utilization
        const utilizationPercent = (totalTBC / tbcCap) * 100;
        if (utilizationPercent >= 90 && utilizationPercent <= 100) {
            warnings.push({
                field: 'tbc',
                severity: this.SEVERITY.WARNING,
                message: `TBC utilization at ${utilizationPercent.toFixed(1)}%`,
                remedy: 'Limited pension capacity remaining',
                code: 'TBC_HIGH_UTILIZATION'
            });
        }

        return { errors, warnings, isValid: errors.length === 0, utilizationPercent };
    },

    // LRBA validation
    validateLRBA(propertyValue, loanAmount, smsfBalance, propertyType) {
        const errors = [];
        const warnings = [];
        const info = [];

        const lvr = (loanAmount / propertyValue) * 100;
        const maxLVR = (FinancialConfig?.SMSF?.BORROWING?.LRBA?.MAXIMUM_LOAN_TO_VALUE ?? 0.70) * 100;

        // LVR check
        if (lvr > maxLVR) {
            errors.push({
                field: 'loanAmount',
                severity: this.SEVERITY.ERROR,
                message: `LVR of ${lvr.toFixed(1)}% exceeds maximum ${maxLVR}%`,
                remedy: `Reduce loan by $${(loanAmount - (propertyValue * (maxLVR / 100))).toLocaleString()} or increase deposit`,
                code: 'LVR_EXCEEDED'
            });
        }

        // Deposit adequacy
        const depositRequired = propertyValue - loanAmount;
        const costsEstimate = propertyValue * 0.07; // ~7% for stamp duty and costs
        const totalUpfront = depositRequired + costsEstimate;

        if (totalUpfront > smsfBalance) {
            errors.push({
                field: 'smsfBalance',
                severity: this.SEVERITY.ERROR,
                message: `Insufficient SMSF balance for LRBA`,
                remedy: `Need $${totalUpfront.toLocaleString()} but have $${smsfBalance.toLocaleString()}`,
                code: 'INSUFFICIENT_BALANCE'
            });
        }

        // Minimum property value warning
        if (propertyValue < 200000) {
            warnings.push({
                field: 'propertyValue',
                severity: this.SEVERITY.WARNING,
                message: 'Property value below typical lender minimum',
                remedy: 'Many lenders require minimum $200,000 property value',
                code: 'LOW_PROPERTY_VALUE'
            });
        }

        // Single acquirable asset reminder
        info.push({
            field: 'lrba',
            severity: this.SEVERITY.INFO,
            message: 'LRBA must be for single acquirable asset',
            remedy: 'Each property requires separate LRBA structure',
            code: 'SINGLE_ASSET_RULE'
        });

        return { errors, warnings, info, isValid: errors.length === 0, lvr };
    },

    // In-house asset validation
    validateInHouseAssets(inHouseAssetValue, totalAssetValue) {
        const errors = [];
        const warnings = [];

        const inHouseRatio = (inHouseAssetValue / totalAssetValue) * 100;
        const maxInHouseRatio = 5;

        if (inHouseRatio > maxInHouseRatio) {
            errors.push({
                field: 'inHouseAssets',
                severity: this.SEVERITY.ERROR,
                message: `In-house assets (${inHouseRatio.toFixed(1)}%) exceed ${maxInHouseRatio}% limit`,
                remedy: `Reduce in-house assets by $${(inHouseAssetValue - (totalAssetValue * 0.05)).toLocaleString()}`,
                code: 'IN_HOUSE_LIMIT_EXCEEDED'
            });
        }

        // Warning at 4%
        if (inHouseRatio >= 4 && inHouseRatio <= 5) {
            warnings.push({
                field: 'inHouseAssets',
                severity: this.SEVERITY.WARNING,
                message: `In-house assets at ${inHouseRatio.toFixed(1)}% (limit is 5%)`,
                remedy: 'Monitor closely to avoid exceeding limit',
                code: 'IN_HOUSE_HIGH'
            });
        }

        return { errors, warnings, isValid: errors.length === 0, inHouseRatio };
    },

    // Investment strategy validation
    validateInvestmentStrategy(allocations, memberAge, riskTolerance) {
        const errors = [];
        const warnings = [];
        const info = [];

        // Check allocations sum to 100%
        const total = Object.values(allocations).reduce((sum, val) => sum + val, 0);
        if (Math.abs(total - 100) > 0.01) {
            errors.push({
                field: 'allocations',
                severity: this.SEVERITY.ERROR,
                message: `Asset allocations must sum to 100% (currently ${total.toFixed(1)}%)`,
                remedy: 'Adjust allocations to total 100%',
                code: 'ALLOCATION_SUM_ERROR'
            });
        }

        // Age-appropriate allocation warnings
        const growthAssets = (allocations.australian_shares || 0) + 
                           (allocations.international_shares_ex_us || 0) + 
                           (allocations.us_shares || 0);
        
        if (memberAge >= 65 && growthAssets > 70) {
            warnings.push({
                field: 'allocations',
                severity: this.SEVERITY.WARNING,
                message: `High growth allocation (${growthAssets.toFixed(1)}%) for retirement phase`,
                remedy: 'Consider reducing growth assets as you near/enter retirement',
                code: 'HIGH_GROWTH_RETIREMENT'
            });
        }

        if (memberAge < 40 && growthAssets < 50) {
            info.push({
                field: 'allocations',
                severity: this.SEVERITY.INFO,
                message: `Conservative allocation for young investor (${growthAssets.toFixed(1)}% growth)`,
                remedy: 'Consider higher growth allocation for long-term wealth building',
                code: 'LOW_GROWTH_YOUNG'
            });
        }

        // Risk tolerance alignment
        if (riskTolerance === 'LOW' && growthAssets > 60) {
            warnings.push({
                field: 'allocations',
                severity: this.SEVERITY.WARNING,
                message: 'Asset allocation inconsistent with low risk tolerance',
                remedy: 'Consider reducing growth assets or reassessing risk tolerance',
                code: 'RISK_MISMATCH'
            });
        }

        if (riskTolerance === 'HIGH' && growthAssets < 50) {
            warnings.push({
                field: 'allocations',
                severity: this.SEVERITY.WARNING,
                message: 'Conservative allocation for high risk tolerance',
                remedy: 'Consider increasing growth assets to match risk profile',
                code: 'ALLOCATION_CONSERVATIVE'
            });
        }

        return { errors, warnings, info, isValid: errors.length === 0 };
    },

    // Member validation
    validateMember(memberData) {
        const errors = [];
        const warnings = [];

        // Required fields
        if (!memberData.name || memberData.name.trim() === '') {
            errors.push({
                field: 'name',
                severity: this.SEVERITY.ERROR,
                message: 'Member name is required',
                remedy: 'Enter member name',
                code: 'NAME_REQUIRED'
            });
        }

        // Age validation
        if (!memberData.age || memberData.age < 0) {
            errors.push({
                field: 'age',
                severity: this.SEVERITY.ERROR,
                message: 'Valid age is required',
                remedy: 'Enter member age',
                code: 'AGE_REQUIRED'
            });
        } else if (memberData.age < 18) {
            warnings.push({
                field: 'age',
                severity: this.SEVERITY.WARNING,
                message: 'Member under 18 - special rules may apply',
                remedy: 'Ensure compliance with child member requirements',
                code: 'MINOR_MEMBER'
            });
        } else if (memberData.age > 100) {
            warnings.push({
                field: 'age',
                severity: this.SEVERITY.WARNING,
                message: 'Unusual age entered',
                remedy: 'Verify age is correct',
                code: 'AGE_UNUSUAL'
            });
        }

        // Balance validation
        if (memberData.balance < 0) {
            errors.push({
                field: 'balance',
                severity: this.SEVERITY.ERROR,
                message: 'Balance cannot be negative',
                remedy: 'Enter valid balance',
                code: 'NEGATIVE_BALANCE'
            });
        }

        // TFN validation
        if (memberData.tfn && !/^\d{9}$/.test(memberData.tfn.replace(/\s/g, ''))) {
            errors.push({
                field: 'tfn',
                severity: this.SEVERITY.ERROR,
                message: 'Invalid TFN format',
                remedy: 'TFN must be 9 digits',
                code: 'INVALID_TFN'
            });
        }

        return { errors, warnings, isValid: errors.length === 0 };
    },

    // Fund details validation
    validateFundDetails(fundData) {
        const errors = [];
        const warnings = [];

        // Fund name
        if (!fundData.fundName || fundData.fundName.trim() === '') {
            errors.push({
                field: 'fundName',
                severity: this.SEVERITY.ERROR,
                message: 'Fund name is required',
                remedy: 'Enter SMSF name',
                code: 'FUND_NAME_REQUIRED'
            });
        }

        // ABN validation (11 digits)
        if (fundData.abn && !/^\d{11}$/.test(fundData.abn.replace(/\s/g, ''))) {
            errors.push({
                field: 'abn',
                severity: this.SEVERITY.ERROR,
                message: 'Invalid ABN format',
                remedy: 'ABN must be 11 digits',
                code: 'INVALID_ABN'
            });
        }

        // TFN validation (9 digits)
        if (fundData.tfn && !/^\d{9}$/.test(fundData.tfn.replace(/\s/g, ''))) {
            errors.push({
                field: 'tfn',
                severity: this.SEVERITY.ERROR,
                message: 'Invalid TFN format',
                remedy: 'TFN must be 9 digits',
                code: 'INVALID_TFN'
            });
        }

        // Member count (1-6 for SMSF)
        if (fundData.numberOfMembers < 1 || fundData.numberOfMembers > 6) {
            errors.push({
                field: 'numberOfMembers',
                severity: this.SEVERITY.ERROR,
                message: 'SMSF must have 1-6 members',
                remedy: 'Enter valid member count',
                code: 'INVALID_MEMBER_COUNT'
            });
        }

        // Trustee count must equal or exceed member count for individual trustees
        if (fundData.trusteeType === 'individual' && fundData.trusteeCount < fundData.numberOfMembers) {
            errors.push({
                field: 'trusteeCount',
                severity: this.SEVERITY.ERROR,
                message: 'Individual trustees must equal or exceed member count',
                remedy: 'All members must be trustees (or appoint additional trustees)',
                code: 'INSUFFICIENT_TRUSTEES'
            });
        }

        return { errors, warnings, isValid: errors.length === 0 };
    },

    // Fee validation
    validateFees(fees, fundBalance) {
        const errors = [];
        const warnings = [];

        // Negative fee check
        Object.entries(fees).forEach(([key, value]) => {
            if (value < 0) {
                errors.push({
                    field: key,
                    severity: this.SEVERITY.ERROR,
                    message: `${key} cannot be negative`,
                    remedy: 'Enter valid fee amount',
                    code: 'NEGATIVE_FEE'
                });
            }
        });

        // Total fees as percentage of balance
        const totalFees = Object.values(fees).reduce((sum, fee) => sum + fee, 0);
        const feePercentage = (totalFees / fundBalance) * 100;

        if (feePercentage > 2.5) {
            warnings.push({
                field: 'totalFees',
                severity: this.SEVERITY.WARNING,
                message: `Total fees (${feePercentage.toFixed(2)}%) exceed typical range`,
                remedy: 'Consider reviewing fee arrangements - typical range is 1-2%',
                code: 'HIGH_FEES'
            });
        }

        // Individual fee reasonableness
        if (fees.auditFees && fees.auditFees > 5000) {
            warnings.push({
                field: 'auditFees',
                severity: this.SEVERITY.WARNING,
                message: 'Audit fees appear high',
                remedy: 'Typical audit fees: $1,000-$3,000',
                code: 'HIGH_AUDIT_FEES'
            });
        }

        return { errors, warnings, isValid: errors.length === 0, feePercentage };
    },

    // Division 293 validation
    validateDivision293(income, contributions) {
        const errors = [];
        const warnings = [];
        const info = [];

        const threshold = 250000;
        
        if (income > threshold) {
            const excessIncome = income - threshold;
            const taxableAmount = Math.min(contributions, excessIncome);
            const div293Tax = taxableAmount * 0.15;

            warnings.push({
                field: 'division293',
                severity: this.SEVERITY.WARNING,
                message: `Division 293 tax applies: $${div293Tax.toLocaleString()}`,
                remedy: 'Consider reducing concessional contributions to minimize Division 293 tax',
                code: 'DIV293_APPLICABLE'
            });
        } else if (income > threshold * 0.9) {
            info.push({
                field: 'division293',
                severity: this.SEVERITY.INFO,
                message: 'Approaching Division 293 threshold',
                remedy: 'Monitor income to avoid unexpected Division 293 tax',
                code: 'DIV293_APPROACHING'
            });
        }

        return { errors, warnings, info, isValid: errors.length === 0 };
    },

    // Comprehensive validation
    validateAll(smsfData) {
        const allErrors = [];
        const allWarnings = [];
        const allInfo = [];

        // Run all validations
        const fundValidation = this.validateFundDetails(smsfData);
        allErrors.push(...fundValidation.errors);
        allWarnings.push(...fundValidation.warnings);

        if (smsfData.contributions) {
            const contribValidation = this.validateContributions(
                smsfData.contributions.concessional || 0,
                smsfData.contributions.nonConcessional || 0,
                smsfData.totalSuperBalance || 0,
                smsfData.age || 45
            );
            allErrors.push(...contribValidation.errors);
            allWarnings.push(...contribValidation.warnings);
            allInfo.push(...contribValidation.info);
        }

        if (smsfData.members && Array.isArray(smsfData.members)) {
            smsfData.members.forEach((member, index) => {
                const memberValidation = this.validateMember(member);
                memberValidation.errors.forEach(err => {
                    allErrors.push({ ...err, memberIndex: index });
                });
                memberValidation.warnings.forEach(warn => {
                    allWarnings.push({ ...warn, memberIndex: index });
                });
            });
        }

        if (smsfData.fees) {
            const feeValidation = this.validateFees(smsfData.fees, smsfData.balance || 0);
            allErrors.push(...feeValidation.errors);
            allWarnings.push(...feeValidation.warnings);
        }

        return {
            errors: allErrors,
            warnings: allWarnings,
            info: allInfo,
            isValid: allErrors.length === 0,
            errorCount: allErrors.length,
            warningCount: allWarnings.length,
            infoCount: allInfo.length
        };
    }
};

// React hook for real-time validation
export const useValidation = (value, validator, dependencies = []) => {
    const [validation, setValidation] = React.useState({ errors: [], warnings: [], info: [], isValid: true });

    React.useEffect(() => {
        if (validator && typeof validator === 'function') {
            const result = validator(value);
            setValidation(result);
        }
    }, [value, ...dependencies]);

    return validation;
};

// Validation helper components
export const ValidationMessage = ({ message, severity }) => {
    const colors = {
        error: 'bg-red-50 border-red-500 text-red-700',
        warning: 'bg-yellow-50 border-yellow-500 text-yellow-700',
        info: 'bg-blue-50 border-blue-500 text-blue-700'
    };

    const icons = {
        error: '❌',
        warning: '⚠️',
        info: 'ℹ️'
    };

    return (
        <div className={`border-l-4 p-3 rounded-r-lg ${colors[severity]} text-sm mt-2`}>
            <div className="flex items-start">
                <span className="mr-2">{icons[severity]}</span>
                <div className="flex-1">
                    <p className="font-medium">{message.message}</p>
                    {message.remedy && (
                        <p className="text-xs mt-1 opacity-90">{message.remedy}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SMSFValidationEngine;
